import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Webhook, ShieldAlert, RefreshCw, CheckCircle2, MinusCircle, AlertTriangle } from "lucide-react";
import { api, fmtDateTime } from "@/lib/api";
import { BentoCard } from "@/components/dashboard/BentoCard";
import { HexBadge } from "@/components/dashboard/Hex";
import { useAuth } from "@/lib/auth";

const STATUS_TONE = {
  handled: "success",
  ignored: "warning",
  signature_failed: "error",
};

function StatCard({ label, value, tone }) {
  return (
    <div className="bg-white border-2 border-black p-4 shadow-brutal-sm">
      <div className="data-label">{label}</div>
      <div className={`font-display text-3xl font-bold ${tone || ""}`}>{value}</div>
    </div>
  );
}

export default function WebhooksPage() {
  const { user, loading } = useAuth();
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["admin-stripe-events"],
    queryFn: async () => (await api.get("/admin/stripe/events?limit=100")).data,
    refetchInterval: 20000,
    enabled: !!user?.is_admin,
    retry: false,
  });

  if (loading) {
    return <p className="text-zinc-500" data-testid="webhooks-loading-auth">Loading…</p>;
  }

  if (!user?.is_admin) {
    return (
      <div data-testid="webhooks-forbidden" className="max-w-lg">
        <BentoCard className="text-center py-12">
          <ShieldAlert size={48} className="mx-auto text-red-500" strokeWidth={1.5} />
          <p className="mt-3 font-display text-lg font-semibold">Admins only</p>
          <p className="text-sm text-zinc-500">
            This page shows Stripe webhook delivery health and is restricted to Lien Scout admins.
          </p>
        </BentoCard>
      </div>
    );
  }

  const counts = data?.counts || {};
  const events = data?.events || [];

  return (
    <div data-testid="webhooks-root" className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="data-label">Admin · Billing</div>
          <h1 className="font-display text-4xl font-bold tracking-tight flex items-center gap-3">
            <Webhook size={30} className="text-red-600" /> Webhook Health
          </h1>
          <p className="text-sm text-zinc-600 mt-2">
            Recent Stripe events received by this app, newest first. Mode:{" "}
            <span className="font-mono-data font-semibold uppercase">{data?.stripe_mode || "—"}</span>
          </p>
        </div>
        <button
          data-testid="webhooks-refresh-btn"
          onClick={() => refetch()}
          className="flex items-center gap-2 border-2 border-black bg-yellow-400 px-4 py-2 font-display font-semibold text-sm shadow-brutal-sm hover:bg-yellow-300"
        >
          <RefreshCw size={14} className={isFetching ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="webhooks-stats">
        <StatCard label="Total received" value={counts.total ?? 0} />
        <StatCard label="Handled" value={counts.handled ?? 0} tone="text-emerald-600" />
        <StatCard label="Ignored" value={counts.ignored ?? 0} tone="text-zinc-500" />
        <StatCard label="Signature failed" value={counts.signature_failed ?? 0}
          tone={counts.signature_failed ? "text-red-600" : ""} />
      </div>

      {data?.last_received_at && (
        <p className="text-xs font-mono-data text-zinc-500">
          Last event received: {fmtDateTime(data.last_received_at)}
        </p>
      )}

      {isLoading && <p className="text-zinc-500">Loading events…</p>}
      {isError && (
        <BentoCard className="text-center py-8">
          <AlertTriangle size={40} className="mx-auto text-red-500" strokeWidth={1.5} />
          <p className="mt-2 font-display font-semibold">Couldn’t load events</p>
          <p className="text-sm text-zinc-500">You may not have admin access.</p>
        </BentoCard>
      )}

      {!isLoading && !isError && events.length === 0 && (
        <BentoCard className="text-center py-12" data-testid="webhooks-empty">
          <Webhook size={48} className="mx-auto text-yellow-400" strokeWidth={1.5} />
          <p className="mt-3 font-display text-lg font-semibold">No events yet</p>
          <p className="text-sm text-zinc-500">
            When Stripe delivers a webhook (checkout, subscription changes), it will appear here.
          </p>
        </BentoCard>
      )}

      {events.length > 0 && (
        <div className="bg-white border-2 border-black overflow-hidden" data-testid="webhooks-table">
          <div className="hidden md:grid grid-cols-[1.4fr_1fr_0.6fr_1fr_1fr] gap-2 px-4 py-3 bg-brand-black text-white font-mono-data text-[10px] uppercase tracking-widest">
            <div>Event type</div>
            <div>Status</div>
            <div>Mode</div>
            <div>Created (Stripe)</div>
            <div>Received</div>
          </div>
          {events.map((ev, i) => {
            const StatusIcon = ev.status === "handled" ? CheckCircle2
              : ev.status === "ignored" ? MinusCircle : AlertTriangle;
            return (
              <div
                key={ev.event_id || i}
                data-testid={`webhook-event-row-${i}`}
                className="grid grid-cols-2 md:grid-cols-[1.4fr_1fr_0.6fr_1fr_1fr] gap-2 px-4 py-3 border-t-2 border-zinc-100 items-center text-sm"
              >
                <div className="font-mono-data font-semibold break-all">{ev.type}</div>
                <div className="flex items-center gap-2">
                  <StatusIcon
                    size={15}
                    className={ev.status === "handled" ? "text-emerald-600"
                      : ev.status === "ignored" ? "text-zinc-400" : "text-red-500"}
                  />
                  <HexBadge tone={STATUS_TONE[ev.status] || "warning"}>{ev.status}</HexBadge>
                </div>
                <div className="font-mono-data text-xs uppercase text-zinc-500">
                  {ev.livemode === true ? "live" : ev.livemode === false ? "test" : "—"}
                </div>
                <div className="font-mono-data text-xs text-zinc-600">
                  {ev.created ? fmtDateTime(ev.created) : "—"}
                </div>
                <div className="font-mono-data text-xs text-zinc-600">
                  {fmtDateTime(ev.received_at)}
                  {ev.error ? (
                    <div className="text-[11px] text-red-700 break-all mt-1">{ev.error}</div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
